import { useCallback, useMemo, useState } from 'react';
import { Button, Grid, Input, Modal, Select, Typography } from 'antd';
import { UndoOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ZoneDropdown from './PrewokZonesDropdown';
import { Gender, VALID_ABHYASI_ID_REGEX } from '../../../shared/Constants';
import { useAuth } from '../../../hooks/useAuth';
import { PreworkApplicationFilters } from '../../../shared/PreWorkServiceTypes';
import { useBatchId } from '../../../hooks/useBatchId';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface FilterProps {
	value: PreworkApplicationFilters;
	onFilterChange: (value: PreworkApplicationFilters) => void;
}

export const Filter = ({
	value: { applicantName, gender, applicantSrcmId },
	onFilterChange,
}: FilterProps) => {
	const { t, i18n } = useTranslation();
	const { srcmId } = useAuth();
	const { batchId } = useBatchId();
	const lengthyLanguages = ['ta', 'ma', 'te', 'ka'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const [isFilterPopup, setIsFilterPopup] = useState<boolean>(false);
	const [isFilterTextApplied, setIsFilterTextApplied] =
		useState<boolean>(false);
	const [nameValue, setNameValue] = useState<string | undefined>(applicantName);
	const [genderValue, setGenderValue] = useState<string | undefined>(gender);
	const [zoneIds, setZoneIds] = useState<Array<number> | undefined>();
	const [zoneNames, setZoneNames] = useState<Array<string> | undefined>();
	const [applicantSrcmIdValue, setApplicantSrcmId] = useState<
		string | undefined
	>(applicantSrcmId);
	const [applicantSrcmIdError, setApplicantSrcmIdError] = useState('');

	const genderDropdownOptions = useMemo(() => {
		return [
			{
				value: Gender.NOT_PROVIDED,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('bothGenders')}
					</p>
				),
			},
			{
				value: Gender.MALE,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('male')}
					</p>
				),
			},
			{
				value: Gender.FEMALE,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('female')}
					</p>
				),
			},
		];
	}, [t]);

	const onApplicantNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setNameValue(e.target.value);
		},
		[]
	);

	const handleZoneChange = useCallback(
		(value: Array<{ value: number; label: string }>) => {
			setZoneIds(value.map((z) => z.value));
			setZoneNames(value.map((z) => z.label));
		},
		[]
	);

	const handleGenderChange = useCallback((value: string) => {
		setGenderValue(value);
	}, []);

	const onApplicantSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.toUpperCase();
			setApplicantSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setApplicantSrcmIdError('invalidAbhyasiId');
			} else {
				setApplicantSrcmIdError('');
			}
		},
		[]
	);

	const handleFilterPopup = useCallback(() => {
		setIsFilterPopup(true);
	}, []);

	const onClose = useCallback(() => {
		setIsFilterPopup(false);
	}, []);

	const handleResetFilters = useCallback(() => {
		setGenderValue('NOT_PROVIDED');
		setNameValue('');
		setZoneIds([]);
		setZoneNames([]);
		setApplicantSrcmId('');
		setApplicantSrcmIdError('');

		onFilterChange({});

		setIsFilterPopup(false);
		setIsFilterTextApplied(false);
	}, [onFilterChange]);

	const handleFilterButtonClick = useCallback(() => {
		onFilterChange({
			applicantSrcmId: applicantSrcmIdValue,
			applicantName: nameValue,
			gender: genderValue,
			zoneNames,
		});
		setIsFilterPopup(false);
		setIsFilterTextApplied(true);
	}, [applicantSrcmIdValue, genderValue, nameValue, onFilterChange, zoneNames]);

	return (
		<>
			<div className="flex justify-end sm:w-auto">
				<Button
					className={`text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5 ${
						isFilterTextApplied ? 'border-primary-blue' : 'border-gray-200'
					} ${isMobile ? 'w-full' : ''}`}
					variant="outlined"
					onClick={handleFilterPopup}
				>
					{isFilterTextApplied ? t('filterApplied') : t('filter')}
				</Button>

				{isFilterTextApplied ? (
					<Button
						className="hidden sm:block flex-1 h-9 text-red-500 font-sans font-bold"
						type="link"
						onClick={handleResetFilters}
					>
						<span>
							{t('resetFilters')}
							<UndoOutlined className="ml-2" />
						</span>
					</Button>
				) : (
					''
				)}
			</div>
			<Modal
				open={isFilterPopup}
				onCancel={onClose}
				footer={null}
				centered
				className={
					isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
				}
				closable={true}
				maskClosable={true}
			>
				<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
					{t('filter')}
				</div>

				<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
					<div className="w-full sm:w-[calc(50%-0.5rem)]">
						<div className="mb-[16px] flex flex-col gap-2">
							<Text className="block text-xs text-gray-650">
								{t('filterByZone')}
							</Text>
							<ZoneDropdown
								functionarySrcmId={srcmId}
								value={zoneIds}
								onChange={handleZoneChange}
								batchId={batchId}
							/>
						</div>
					</div>

					<div className="w-full sm:w-[calc(50%-0.5rem)]">
						<div className="mb-[16px] flex flex-col gap-2">
							<Text className="text-xs text-gray-650">
								{t('filterByGender')}
							</Text>
							<Select
								className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
								value={genderValue ?? 'NOT_PROVIDED'}
								options={genderDropdownOptions}
								onChange={handleGenderChange}
							/>
						</div>
					</div>
					<div className="w-full sm:w-[calc(50%-0.5rem)]">
						<div className="mb-[16px] flex flex-col gap-2">
							<Text className="text-xs text-gray-650">
								{t('filterByApplicantName')}
							</Text>
							<Input
								className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
								value={nameValue}
								allowClear
								onChange={onApplicantNameChange}
							/>
						</div>
					</div>
					<div className="w-full sm:w-[calc(50%-0.5rem)]">
						<div className="mb-[16px] flex flex-col gap-2">
							<Text className="text-xs text-gray-650">
								{t('filterByApplicantSRCMId')}
							</Text>
							<Input
								className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
								value={applicantSrcmIdValue}
								allowClear
								onChange={onApplicantSRCMIdChange}
							/>
							{applicantSrcmIdError && (
								<Text className="text-xs text-red-500">
									{t(applicantSrcmIdError)}
								</Text>
							)}
						</div>
					</div>
					<div
						className={`flex ${
							isStackVertical
								? 'flex-col items-center space-y-2'
								: 'justify-center gap-2'
						} mt-4 ant-modal-footer`}
					>
						<Button
							className="hidden sm:block flex-1 h-9 text-primary-blue font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
						<Button
							className={`flex-1 h-9 text-primary-blue font-sans font-bold ${
								isStackVertical ? 'p-2' : ''
							}`}
							onClick={onClose}
						>
							{t('closeFilter')}
						</Button>
						<Button
							type="primary"
							onClick={handleFilterButtonClick}
							className={`flex-1 h-9 font-sans font-bold ${
								isStackVertical ? 'p-2 mt-3' : ''
							}`}
							disabled={applicantSrcmIdError !== ''}
						>
							{t('applyFilter')}
						</Button>
					</div>
				</div>
			</Modal>
		</>
	);
};
